/**
 * EventTypeFilter Component
 *
 * Checkbox filter for event types in guest view.
 * Categories:
 * - Concerts: Events with music_genre set (non-other)
 * - Sporting Events: Dynamo, Dash, or contains "Rockets"
 * - Others: Everything else
 *
 * All checkboxes are pre-selected by default.
 */

import React from 'react';
import { Music, Trophy, Calendar } from 'lucide-react';

export type EventCategory = 'concerts' | 'sports' | 'others';

export interface EventTypeFilters {
  concerts: boolean;
  sports: boolean;
  others: boolean;
}

interface EventTypeFilterProps {
  filters: EventTypeFilters;
  onChange: (filters: EventTypeFilters) => void;
  /** Compact mode for mobile */
  compact?: boolean;
}

export const EventTypeFilter: React.FC<EventTypeFilterProps> = ({
  filters,
  onChange,
  compact = false,
}) => {
  const handleToggle = (category: EventCategory) => {
    onChange({
      ...filters,
      [category]: !filters[category],
    });
  };

  const categories = [
    {
      key: 'concerts' as EventCategory,
      label: 'Concerts',
      icon: Music,
      activeColor: 'bg-purple-500/20 border-purple-500/50 text-purple-400',
      inactiveColor: 'bg-dark-bg border-dark-border/30 text-dark-muted',
    },
    {
      key: 'sports' as EventCategory,
      label: 'Sporting Events',
      icon: Trophy,
      activeColor: 'bg-orange-500/20 border-orange-500/50 text-orange-400',
      inactiveColor: 'bg-dark-bg border-dark-border/30 text-dark-muted',
    },
    {
      key: 'others' as EventCategory,
      label: 'Others',
      icon: Calendar,
      activeColor: 'bg-sky-500/20 border-sky-500/50 text-sky-400',
      inactiveColor: 'bg-dark-bg border-dark-border/30 text-dark-muted',
    },
  ];

  if (compact) {
    // Compact horizontal layout for mobile
    return (
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {categories.map(({ key, label, icon: Icon, activeColor, inactiveColor }) => (
          <button
            key={key}
            onClick={() => handleToggle(key)}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium
              transition-all duration-150 whitespace-nowrap
              ${filters[key] ? activeColor : inactiveColor}
            `}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>
    );
  }

  // Full layout with checkboxes
  return (
    <div className="flex flex-wrap items-center gap-3">
      {categories.map(({ key, label, icon: Icon, activeColor, inactiveColor }) => (
        <label
          key={key}
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer
            transition-all duration-150 select-none
            ${filters[key] ? activeColor : inactiveColor}
          `}
        >
          <input
            type="checkbox"
            checked={filters[key]}
            onChange={() => handleToggle(key)}
            className="sr-only"
          />
          <div className={`
            w-4 h-4 rounded border-2 flex items-center justify-center
            transition-all duration-150
            ${filters[key]
              ? 'bg-current border-current'
              : 'border-gray-500 bg-transparent'
            }
          `}>
            {filters[key] && (
              <svg className="w-3 h-3 text-dark-bg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <Icon className="w-4 h-4" />
          <span className="text-sm font-medium">{label}</span>
        </label>
      ))}
    </div>
  );
};

/**
 * Default filter state - all categories selected
 */
export const defaultEventTypeFilters: EventTypeFilters = {
  concerts: true,
  sports: true,
  others: true,
};

/**
 * Utility to categorize an event
 * Uses centralized eventTypeDetector utility
 */
import type { HMCEvent } from '../hmcEventsStore';
import { getEventFilterCategory, type EventFilterCategory } from '../utils/eventTypeDetector';

// Re-export from utility for backward compatibility
export const getEventCategory = (event: HMCEvent): EventCategory => {
  return getEventFilterCategory(event);
};

/**
 * Filter events based on active filters
 */
export const filterEventsByType = (
  events: HMCEvent[],
  filters: EventTypeFilters
): HMCEvent[] => {
  return events.filter((event) => {
    const category = getEventCategory(event);
    return filters[category];
  });
};

export default EventTypeFilter;
